import React, { useState, useMemo } from 'react';
import Pagination from './Pagination';
import './DataGrid.css';

/**
 * DataGrid - Table with Search, Filter, and Pagination.
 * @param {Array} data - Full list of rows
 * @param {Array} columns - [{ key, label, render?: (value, row) => node }]
 * @param {string} searchPlaceholder - Placeholder for search input
 * @param {Array<string>} searchKeys - Object keys to search in (e.g. ['name', 'code'])
 * @param {Array} filters - [{ key, label, options: [{ value, label }] }] optional
 * @param {number} defaultPageSize - Default page size (default 10)
 * @param {string} emptyMessage - Message when no data
 * @param {string} className - Table wrapper class
 * @param {string} tableClassName - Optional class for the <table> (e.g. sites-onboarding-table)
 * @param {Function} renderRow - Optional custom row render (row, index) => <tr>...</tr>
 * @param {ReactNode|Function} children - Optional; if function, receives (paginatedData) for custom body or card grid
 * @param {string} gridLayout - 'table' (default) | 'custom' (render children(paginatedData) only, no table)
 */
export const DataGrid = ({
  data = [],
  columns = [],
  searchPlaceholder = '',
  searchKeys = [],
  filters = [],
  defaultPageSize = 10,
  emptyMessage = 'No data to display.',
  className = '',
  tableClassName = '',
  renderRow,
  children,
  gridLayout = 'table',
}) => {
  const [searchText, setSearchText] = useState('');
  const [filterValues, setFilterValues] = useState({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const filteredData = useMemo(() => {
    let result = Array.isArray(data) ? [...data] : [];
    if (searchText.trim() && searchKeys.length > 0) {
      const q = searchText.trim().toLowerCase();
      result = result.filter((row) => {
        return searchKeys.some((key) => {
          const val = row[key] ?? row[key.charAt(0).toUpperCase() + key.slice(1)];
          return String(val ?? '').toLowerCase().includes(q);
        });
      });
    }
    filters.forEach((f) => {
      const v = filterValues[f.key];
      if (v != null && v !== '') {
        result = result.filter((row) => {
          const val = row[f.key] ?? row[(f.key.charAt(0).toUpperCase() + f.key.slice(1))];
          return String(val ?? '') === String(v);
        });
      }
    });
    return result;
  }, [data, searchText, searchKeys, filters, filterValues]);

  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const paginatedData = filteredData.slice(start, start + pageSize);

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const renderTableBody = () => {
    if (paginatedData.length === 0) {
      return (
        <tr>
          <td colSpan={columns.length || 1} className="data-grid-empty">
            {emptyMessage}
          </td>
        </tr>
      );
    }
    if (typeof children === 'function') return children(paginatedData);
    if (renderRow) return paginatedData.map((row, i) => renderRow(row, start + i));
    return paginatedData.map((row, index) => (
      <tr key={row.id ?? row.code ?? index}>
        {columns.map((col) => (
          <td key={col.key}>
            {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
          </td>
        ))}
      </tr>
    ));
  };

  const showToolbar = searchKeys.length > 0 || filters.length > 0 || columns.length > 0;

  return (
    <div className={`data-grid ${className}`}>
      {showToolbar && (
        <div className="data-grid-toolbar">
          <div className="data-grid-controls">
            {searchKeys.length > 0 && (
              <div className="data-grid-search">
                <input
                  type="text"
                  className="form-input data-grid-search-input"
                  placeholder={searchPlaceholder || 'Search'}
                  value={searchText}
                  onChange={(e) => {
                    setSearchText(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            )}
            {filters.map((f) => (
              <div key={f.key} className="data-grid-filter">
                <label className="data-grid-filter-label">{f.label}</label>
                <select
                  className="form-select data-grid-filter-select"
                  value={filterValues[f.key] ?? ''}
                  onChange={(e) => handleFilterChange(f.key, e.target.value)}
                >
                  <option value="">All</option>
                  {(f.options || []).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            {columns.length > 0 && (
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="form-select page-size-select data-grid-page-size"
              >
                <option value={5}>5 items</option>
                <option value={10}>10 items</option>
                <option value={25}>25 items</option>
                <option value={50}>50 items</option>
              </select>
            )}
          </div>
        </div>
      )}

      {gridLayout === 'custom' ? (
        <div className="data-grid-custom-content">
          {paginatedData.length === 0 ? (
            <p className="data-grid-empty">{emptyMessage}</p>
          ) : (
            typeof children === 'function' ? children(paginatedData) : null
          )}
        </div>
      ) : (
        <div className="data-grid-table-wrapper table-responsive">
          <table className={`data-grid-table table ${tableClassName}`.trim()}>
            {columns.length > 0 && (
              <thead>
                <tr>
                  {columns.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>{renderTableBody()}</tbody>
          </table>
        </div>
      )}

      {totalItems > 0 && (
        <Pagination
          page={currentPage}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showPageSize={false}
        />
      )}
    </div>
  );
};

export default DataGrid;
